import type Cesium from "cesium";
import type {CesiumTerrainProvider} from "cesium";

import Map from "./Map";
import {Coordinate as olCoordinate} from "ol/coordinate";
import {toLonLat} from "ol/proj";

export default class ElevationProvider {
    private terrainProvider: CesiumTerrainProvider|null = null;
    private readonly initializePromise: Promise<void>;
    private CesiumPackage: typeof Cesium;

    constructor(readonly map: Map) {
        this.initializePromise = (async () => {
            this.CesiumPackage = await import('cesium'); // import * as Cesium from "cesium";

            window.Cesium = this.CesiumPackage;
            this.CesiumPackage.Ion.defaultAccessToken = import.meta.env.VITE_CESIUM_ION_TOKEN;

            this.terrainProvider = await this.CesiumPackage.createWorldTerrainAsync();
        })();
    }

    async awaitInitialization() {
        await this.initializePromise;
    }

    getTerrainProvider(): CesiumTerrainProvider {
        return this.terrainProvider;
    }

    async getElevationAtLonLats(lonlats: [number, number][]): Promise<number[]|null> {
        await this.initializePromise;

        const positions = lonlats.map(
            (lonlat: [number, number]) => this.CesiumPackage.Cartographic.fromDegrees(...lonlat)
        );

        let positionsWithHeight: Cesium.Cartographic[];
        try {
            positionsWithHeight = await this.CesiumPackage.sampleTerrainMostDetailed(this.terrainProvider, positions, true);
        } catch {
            return null;
        }

        return positionsWithHeight.map((positionWithHeight) => positionWithHeight.height);
    }

    async getElevationAtLonLat(lonlat: [number, number]): Promise<number|null> {
        const heights = await this.getElevationAtLonLats([lonlat]);
        if (heights === null) {
            return null;
        }
        return heights[0];
    }

    async getElevationAtOlCoordinates(coords: olCoordinate[]): Promise<number[]|null> {
        return this.getElevationAtLonLats(coords.map(coord => toLonLat(coord)));
    }

    async getElevationAtOlCoordinate(coord: olCoordinate): Promise<number|null> {
        const heights = await this.getElevationAtOlCoordinates([coord]);
        if (heights === null) {
            return null;
        }
        return heights[0];
    }
}
